import { z } from "zod";
import { assertIJson } from "./strict-json.js";
import { DocumentRedactionRegionSchema } from "./documents.js";
import { CapabilityManifestSchema, FocusContextKindSchema, FocusSourceBindingSchema, OmpSessionManifestSchema, } from "./focus.js";
import { DecimalStringSchema, Rfc3339MillisSchema, Sha256Base64UrlSchema, Sha256HexSchema, UuidSchema, } from "./primitives.js";
export const DEVICE_PROTOCOL_VERSION = "1";
export const DEVICE_PAIRING_DOMAIN = "mind-omp-pair-v1\0";
export const DEVICE_AUTH_DOMAIN = "mind-omp-auth-v1\0";
export const DEVICE_ENVELOPE_DOMAIN = "mind-omp-envelope-v1\0";
export const DeviceMessageTypeSchema = z.enum([
    "CAPABILITIES",
    "JOB_OFFER",
    "JOB_ACCEPT",
    "JOB_REJECT",
    "JOB_START",
    "JOB_EVENT",
    "JOB_RESULT",
    "RESULT_ACK",
    "JOB_CANCEL",
    "JOB_CANCELLED",
    "HEARTBEAT",
    "LEASE_RENEW",
    "RESUME_INVENTORY",
    "SYNC_OFFER",
    "SYNC_RESULT",
    "MCP_REQUEST",
    "MCP_RESULT",
    "FOCUS_REQUEST_START",
    "FOCUS_REQUEST_CHUNK",
    "FOCUS_DELTA",
    "FOCUS_RESULT_START",
    "FOCUS_RESULT_CHUNK",
    "FOCUS_CANCEL",
]);
export const DeviceEnvelopeHeaderSchema = z
    .object({
    direction: z.enum(["connector_to_server", "server_to_connector"]),
    protocolVersion: z.literal(DEVICE_PROTOCOL_VERSION),
    connectionId: UuidSchema,
    keyId: z.string().min(1).max(128),
    messageId: UuidSchema,
    operationId: UuidSchema,
    sequence: DecimalStringSchema,
    timestamp: Rfc3339MillisSchema,
    type: DeviceMessageTypeSchema,
    bodySha256: Sha256Base64UrlSchema,
})
    .strict();
export const SignedDeviceEnvelopeSchema = z
    .object({
    header: DeviceEnvelopeHeaderSchema,
    body: z.unknown(),
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/),
})
    .strict();
export const RedactionWorkerCapabilitySchema = z
    .object({
    kind: z.literal("redaction_derivative"),
    verifierVersion: z.string().trim().min(1).max(200),
})
    .strict();
export const DeviceCapabilitiesSchema = z
    .object({
    models: z
        .array(z.string().trim().min(1).max(200))
        .max(100)
        .refine((models) => new Set(models).size === models.length, "Duplicate models are not allowed"),
    workerCapabilities: z
        .array(RedactionWorkerCapabilitySchema)
        .max(100)
        .refine((capabilities) => new Set(capabilities.map(({ kind, verifierVersion }) => `${kind}\0${verifierVersion}`)).size
        === capabilities.length, "Duplicate worker capabilities are not allowed"),
    sdkVersion: z.string().trim().min(1).max(200),
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const FocusConnectorPhaseSchema = z.enum(["planner", "public_search", "synthesis"]);
export const FocusConnectorContextSchema = z
    .object({
    kind: FocusContextKindSchema,
    id: UuidSchema,
    revision: DecimalStringSchema,
    text: z.string().max(1_048_576),
})
    .strict();
const FocusConnectorBaseInputShape = {
    runId: UuidSchema,
    branchId: UuidSchema,
    manifest: CapabilityManifestSchema,
    sessionManifest: OmpSessionManifestSchema,
};
export const FocusConnectorInputSchema = z.discriminatedUnion("phase", [
    z.object({
        ...FocusConnectorBaseInputShape,
        phase: z.literal("planner"),
        prompt: z.string().min(1).max(262_144),
        assistantLocale: z.enum(["ru", "en"]),
        context: z.array(FocusConnectorContextSchema).max(1_000),
    }).strict(),
    z.object({
        ...FocusConnectorBaseInputShape,
        phase: z.literal("public_search"),
        queries: z.array(z.string().trim().min(1).max(2_048)).min(1).max(10),
    }).strict(),
    z.object({
        ...FocusConnectorBaseInputShape,
        phase: z.literal("synthesis"),
        prompt: z.string().min(1).max(262_144),
        assistantLocale: z.enum(["ru", "en"]),
        context: z.array(FocusConnectorContextSchema).max(1_000),
        sources: z.array(FocusSourceBindingSchema).max(100),
    }).strict(),
]);
const FocusProposalInputSchema = z.record(z.string(), z.unknown()).superRefine((value, context) => {
    try {
        assertIJson(value);
        if (new TextEncoder().encode(JSON.stringify(value)).byteLength > 262_144) {
            context.addIssue({ code: "custom", message: "Proposal input exceeds byte bound" });
        }
    }
    catch {
        context.addIssue({ code: "custom", message: "Proposal input must be I-JSON" });
    }
});
export const FocusConnectorOutputSchema = z.discriminatedUnion("phase", [
    z.object({
        phase: z.literal("planner"),
        queries: z.array(z.string().trim().min(1).max(2_048)).max(10),
        requiresQueryReview: z.boolean(),
    }).strict(),
    z.object({
        phase: z.literal("public_search"),
        sources: z.array(FocusSourceBindingSchema).min(1).max(100),
    }).strict(),
    z.object({
        phase: z.literal("synthesis"),
        bodyMarkdown: z.string().trim().min(1).max(65_536),
        citations: z.array(z.object({ sourceId: UuidSchema }).strict()).max(100).optional(),
        proposalInput: FocusProposalInputSchema.optional(),
    }).strict(),
]).superRefine((output, context) => {
    if (output.phase === "planner" && output.requiresQueryReview && output.queries.length === 0) {
        context.addIssue({ code: "custom", message: "Query review requires at least one query", path: ["queries"] });
    }
});
export const FocusConnectorResultSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("ok"), output: FocusConnectorOutputSchema }).strict(),
    z.object({
        status: z.literal("failed"),
        errorCode: z.enum(["PROVIDER_UNAVAILABLE", "PROVIDER_FAILED", "SEARCH_FAILED", "CANCELLED"]),
    }).strict(),
]);
const FocusTransferByteLengthSchema = DecimalStringSchema.refine((value) => BigInt(value) > 0n && BigInt(value) <= 9n * 1024n * 1024n, "Focus transfer byte length is out of bounds");
export const FocusTransferStartSchema = z
    .object({
    requestId: UuidSchema,
    byteLength: FocusTransferByteLengthSchema,
    chunkCount: z.number().int().min(1).max(288),
    sha256: Sha256HexSchema,
})
    .strict();
export const FocusTransferChunkSchema = z
    .object({
    requestId: UuidSchema,
    index: z.number().int().min(0).max(287),
    dataBase64: z.string().max(43_691).regex(/^[A-Za-z0-9_-]+$/),
})
    .strict();
export const FocusConnectorDeltaSchema = z
    .object({ requestId: UuidSchema, text: z.string().min(1).max(32_768) })
    .strict();
export const FocusConnectorCancelSchema = z.object({ requestId: UuidSchema }).strict();
export const DevicePairingProofPayloadSchema = z
    .object({
    serverOrigin: z.string().url().refine((value) => value.startsWith("https://"), "HTTPS origin required"),
    protocolVersion: z.literal(DEVICE_PROTOCOL_VERSION),
    deviceKeyId: z.string().min(1).max(128),
    publicKeyBase64: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    tokenSha256: Sha256Base64UrlSchema,
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const DeviceAuthPayloadSchema = z
    .object({
    serverOrigin: z.string().url().refine((value) => value.startsWith("https://"), "HTTPS origin required"),
    protocolVersion: z.literal(DEVICE_PROTOCOL_VERSION),
    connectionId: UuidSchema,
    nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    issuedAt: Rfc3339MillisSchema,
    expiresAt: Rfc3339MillisSchema,
    deviceKeyId: z.string().min(1).max(128),
})
    .strict();
function canonicalizeValue(value) {
    if (value === null || typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
        const encoded = JSON.stringify(value);
        if (encoded === undefined)
            throw new TypeError("Value is not representable as JSON");
        return encoded;
    }
    if (Array.isArray(value))
        return `[${value.map((entry) => canonicalizeValue(entry)).join(",")}]`;
    if (typeof value === "object") {
        const record = value;
        return `{${Object.keys(record)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonicalizeValue(record[key])}`)
            .join(",")}}`;
    }
    throw new TypeError(`Value of type ${typeof value} is not representable as JSON`);
}
export function canonicalizeJcs(value) {
    assertIJson(value);
    return canonicalizeValue(value);
}
export const ConnectorStatusSchema = z.enum(["paired", "offline", "revoked"]);
export const ConnectorProjectionSchema = z
    .object({
    id: UuidSchema,
    accountId: UuidSchema,
    deviceKeyId: z.string().min(1).max(128),
    label: z.string().min(1).max(200),
    status: ConnectorStatusSchema,
    grantEpoch: DecimalStringSchema,
    lastSeenAt: Rfc3339MillisSchema.nullable(),
    createdAt: Rfc3339MillisSchema,
})
    .strict();
export const ConnectorListSchema = z.object({ items: z.array(ConnectorProjectionSchema).max(100) }).strict();
export const CreatePairingTokenCommandSchema = z
    .object({
    commandId: UuidSchema,
    label: z.string().trim().min(1).max(200),
    expiresInSeconds: z.number().int().min(60).max(900).default(600),
    replaceExisting: z.boolean().default(false),
})
    .strict();
export const PairingTokenResultSchema = z
    .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const ConnectorPairMessageSchema = z
    .object({
    type: z.literal("PAIR"),
    pairingToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    deviceKeyId: z.string().min(1).max(128),
    publicKeyBase64: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    expiresAt: Rfc3339MillisSchema,
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/),
})
    .strict();
export const DeviceAuthMessageSchema = z
    .object({
    type: z.literal("AUTH"),
    payload: DeviceAuthPayloadSchema,
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/),
})
    .strict();
export const DeviceChallengeSchema = z
    .object({
    type: z.literal("CHALLENGE"),
    payload: DeviceAuthPayloadSchema,
})
    .strict();
export const DeviceReadySchema = z
    .object({
    type: z.literal("READY"),
    connectorId: UuidSchema,
    accountId: UuidSchema,
    grantEpoch: DecimalStringSchema,
})
    .strict();
export const DeviceHelloMessageSchema = z
    .object({ type: z.literal("HELLO"), deviceKeyId: z.string().min(1).max(128) })
    .strict();
export const DevicePairedSchema = z
    .object({
    type: z.literal("PAIRED"),
    connectorId: UuidSchema,
    accountId: UuidSchema,
    grantEpoch: DecimalStringSchema,
})
    .strict();
export const RevokeConnectorCommandSchema = z
    .object({ commandId: UuidSchema, expectedGrantEpoch: DecimalStringSchema })
    .strict();
export const ConnectorCapabilitySchema = z
    .object({
    name: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    version: z.string().min(1).max(32),
})
    .strict();
export const ConnectorCapabilityManifestSchema = z
    .object({
    manifestId: UuidSchema,
    accountId: UuidSchema,
    grantEpoch: DecimalStringSchema,
    issuedAt: Rfc3339MillisSchema,
    expiresAt: Rfc3339MillisSchema,
    entityIds: z.array(UuidSchema).max(10_000),
    documentVersionIds: z.array(UuidSchema).max(10_000),
    capabilities: z.array(ConnectorCapabilitySchema).max(100),
    byteBudget: DecimalStringSchema,
})
    .strict();
export const ConnectorJobStateSchema = z.enum(["offered", "leased", "running", "succeeded", "failed", "cancelling", "cancelled", "interrupted"]);
export const ConnectorFenceTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const ConnectorJobFenceSchema = z
    .object({
    kind: z.literal("job"),
    attemptId: UuidSchema,
    leaseGeneration: DecimalStringSchema,
    leaseToken: ConnectorFenceTokenSchema,
    deviceGrantEpoch: DecimalStringSchema,
})
    .strict();
export const ConnectorSyncFenceSchema = z
    .object({
    kind: z.literal("sync"),
    syncOperationId: UuidSchema,
    syncGeneration: DecimalStringSchema,
    syncToken: ConnectorFenceTokenSchema,
    deviceGrantEpoch: DecimalStringSchema,
})
    .strict();
export const ConnectorMcpFenceSchema = z
    .object({
    kind: z.literal("mcp"),
    mcpRequestId: UuidSchema,
    requestToken: ConnectorFenceTokenSchema,
    deviceGrantEpoch: DecimalStringSchema,
})
    .strict();
export const ConnectorTransferFenceSchema = z.discriminatedUnion("kind", [
    ConnectorJobFenceSchema,
    ConnectorSyncFenceSchema,
    ConnectorMcpFenceSchema,
]);
export const ConnectorLeaseSchema = z
    .object({
    attemptId: UuidSchema,
    leaseGeneration: DecimalStringSchema,
    leaseToken: ConnectorFenceTokenSchema,
    leaseExpiresAt: Rfc3339MillisSchema,
    deviceGrantEpoch: DecimalStringSchema,
})
    .strict();
const MAX_DEVICE_BLOB_BYTES = 50n * 1024n * 1024n;
const DeviceBlobByteLengthSchema = DecimalStringSchema.refine((value) => BigInt(value) > 0n && BigInt(value) <= MAX_DEVICE_BLOB_BYTES, "Expected a device blob byte length from 1 byte through 50 MiB");
const RedactionJobLineageShape = {
    derivativeId: UuidSchema,
    documentId: UuidSchema,
    sourceVersionId: UuidSchema,
    sourceSha256: Sha256HexSchema,
};
const RedactionVerifierInputShape = {
    sourceSha256: Sha256HexSchema,
    regions: z.array(DocumentRedactionRegionSchema).min(1).max(2_000),
    verifierVersion: z.string().trim().min(1).max(200),
};
export const RedactionVerifierInputSchema = z.object(RedactionVerifierInputShape).strict();
const RedactionJobOfferShape = {
    kind: z.literal("redaction_derivative"),
    jobId: UuidSchema,
    ...RedactionJobLineageShape,
    regions: RedactionVerifierInputShape.regions,
    verifierVersion: RedactionVerifierInputShape.verifierVersion,
};
export const RedactionJobOfferSchema = z.object(RedactionJobOfferShape).strict();
export const RedactionJobAcceptSchema = z
    .object({
    kind: z.literal("redaction_derivative"),
    jobId: UuidSchema,
})
    .strict();
export const DeviceBlobGrantSchema = z
    .object({
    grantId: UuidSchema,
    token: ConnectorFenceTokenSchema,
    method: z.enum(["GET", "PUT"]),
    direction: z.enum(["source", "candidate"]),
    objectId: UuidSchema,
    jobId: UuidSchema,
    attemptId: UuidSchema,
    leaseGeneration: DecimalStringSchema,
    deviceGrantEpoch: DecimalStringSchema,
    maxByteLength: DeviceBlobByteLengthSchema,
    expectedSha256: Sha256HexSchema.nullable(),
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const RedactionJobStartSchema = z
    .object({
    ...RedactionJobOfferShape,
    lease: ConnectorLeaseSchema,
    sourceGrant: DeviceBlobGrantSchema,
    candidateGrant: DeviceBlobGrantSchema,
})
    .strict()
    .superRefine((start, context) => {
    const addIssue = (path, message) => context.addIssue({ code: z.ZodIssueCode.custom, path, message });
    const validateGrantFence = (grant, name) => {
        if (grant.jobId !== start.jobId)
            addIssue([name, "jobId"], "Grant job does not match the job start");
        if (grant.attemptId !== start.lease.attemptId)
            addIssue([name, "attemptId"], "Grant attempt does not match the lease");
        if (grant.leaseGeneration !== start.lease.leaseGeneration) {
            addIssue([name, "leaseGeneration"], "Grant generation does not match the lease");
        }
        if (grant.deviceGrantEpoch !== start.lease.deviceGrantEpoch) {
            addIssue([name, "deviceGrantEpoch"], "Grant epoch does not match the lease");
        }
        if (grant.expiresAt > start.lease.leaseExpiresAt) {
            addIssue([name, "expiresAt"], "Grant cannot outlive the job lease");
        }
    };
    validateGrantFence(start.sourceGrant, "sourceGrant");
    validateGrantFence(start.candidateGrant, "candidateGrant");
    if (start.sourceGrant.method !== "GET")
        addIssue(["sourceGrant", "method"], "Source grant must use GET");
    if (start.sourceGrant.direction !== "source")
        addIssue(["sourceGrant", "direction"], "Source grant direction must be source");
    if (start.sourceGrant.objectId !== start.sourceVersionId) {
        addIssue(["sourceGrant", "objectId"], "Source grant object must be the offered source version");
    }
    if (start.sourceGrant.expectedSha256 !== start.sourceSha256) {
        addIssue(["sourceGrant", "expectedSha256"], "Source grant hash must match the offered source hash");
    }
    if (start.candidateGrant.method !== "PUT")
        addIssue(["candidateGrant", "method"], "Candidate grant must use PUT");
    if (start.candidateGrant.direction !== "candidate") {
        addIssue(["candidateGrant", "direction"], "Candidate grant direction must be candidate");
    }
    if (start.candidateGrant.objectId !== start.derivativeId) {
        addIssue(["candidateGrant", "objectId"], "Candidate grant object must be the offered derivative");
    }
    if (start.candidateGrant.expectedSha256 !== null) {
        addIssue(["candidateGrant", "expectedSha256"], "Candidate hash must be unknown before generation");
    }
    if (start.sourceGrant.grantId === start.candidateGrant.grantId) {
        addIssue(["candidateGrant", "grantId"], "Source and candidate grants must be distinct");
    }
});
export const RedactionJobLeaseRenewSchema = z
    .object({
    kind: z.literal("redaction_derivative"),
    jobId: UuidSchema,
    lease: ConnectorLeaseSchema,
})
    .strict();
export const RedactionJobCancelSchema = z
    .object({
    kind: z.literal("redaction_derivative"),
    jobId: UuidSchema,
    attemptId: UuidSchema,
    leaseGeneration: DecimalStringSchema,
    cancellationToken: ConnectorFenceTokenSchema,
})
    .strict();
export const RedactionJobCancelledSchema = RedactionJobCancelSchema;
const RedactionVerificationPageProofSchema = z
    .object({
    page: z.number().int().min(1).max(500),
    rasterOnly: z.literal(true),
    opaquePixelsCovered: z.literal(true),
    extractedText: z.literal(""),
    originalObjectHashes: z.tuple([]),
})
    .strict();
export const RedactionVerificationProofSchema = z
    .object({
    sourceSha256: Sha256HexSchema,
    derivativeSha256: Sha256HexSchema,
    sourcePageCount: z.number().int().min(1).max(500),
    derivativePageCount: z.number().int().min(1).max(500),
    dpi: z.literal(300),
    verifierVersion: z.string().trim().min(1).max(200),
    networkAccess: z.literal(false),
    hasAttachments: z.literal(false),
    hasActions: z.literal(false),
    hasJavaScript: z.literal(false),
    hasForms: z.literal(false),
    hasOptionalContentGroups: z.literal(false),
    affectedPages: z.array(RedactionVerificationPageProofSchema).min(1).max(500),
})
    .strict()
    .superRefine((proof, context) => {
    if (proof.sourceSha256 === proof.derivativeSha256) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["derivativeSha256"],
            message: "Derivative hash must differ from the source hash",
        });
    }
    if (proof.derivativePageCount !== proof.sourcePageCount) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["derivativePageCount"],
            message: "Redaction must preserve the source page count",
        });
    }
    let previousPage = 0;
    for (const [index, page] of proof.affectedPages.entries()) {
        if (page.page > proof.sourcePageCount) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["affectedPages", index, "page"],
                message: "Affected page exceeds the source page count",
            });
        }
        if (page.page <= previousPage) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["affectedPages", index, "page"],
                message: "Affected pages must be unique and sorted",
            });
        }
        previousPage = page.page;
    }
});
const RedactionJobResultBaseShape = {
    kind: z.literal("redaction_derivative"),
    jobId: UuidSchema,
    lease: ConnectorLeaseSchema,
    ...RedactionJobLineageShape,
    verifierVersion: z.string().trim().min(1).max(200),
};
const VerifiedRedactionJobResultSchema = z
    .object({
    ...RedactionJobResultBaseShape,
    state: z.literal("verified"),
    candidateBlobId: UuidSchema,
    candidateSha256: Sha256HexSchema,
    candidateByteLength: DeviceBlobByteLengthSchema,
    proof: RedactionVerificationProofSchema,
})
    .strict();
const RejectedRedactionJobResultSchema = z
    .object({
    ...RedactionJobResultBaseShape,
    state: z.literal("rejected"),
    candidateBlobId: z.null(),
    candidateSha256: z.null(),
    candidateByteLength: z.null(),
    proof: z.null(),
    errorCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/),
})
    .strict();
export const RedactionJobResultSchema = z
    .discriminatedUnion("state", [VerifiedRedactionJobResultSchema, RejectedRedactionJobResultSchema])
    .superRefine((result, context) => {
    if (result.state === "verified") {
        if (result.proof.sourceSha256 !== result.sourceSha256) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["proof", "sourceSha256"],
                message: "Proof source hash does not match result lineage",
            });
        }
        if (result.proof.derivativeSha256 !== result.candidateSha256) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["proof", "derivativeSha256"],
                message: "Proof derivative hash does not match the candidate hash",
            });
        }
        if (result.proof.verifierVersion !== result.verifierVersion) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["proof", "verifierVersion"],
                message: "Proof verifier version does not match the job result",
            });
        }
    }
});
export const RedactionJobResultAckSchema = z
    .object({
    jobId: UuidSchema,
    attemptId: UuidSchema,
    resultDigest: Sha256Base64UrlSchema,
})
    .strict();
export const ConnectorJobEventSchema = z
    .object({
    attemptId: UuidSchema,
    leaseGeneration: DecimalStringSchema,
    eventSequence: DecimalStringSchema,
    eventType: z.string().regex(/^[a-z][a-z0-9_.-]{0,63}$/),
    payload: z.record(z.string(), z.unknown()),
})
    .strict();
export const ConnectorSyncDirectionSchema = z.enum(["to_device", "from_device"]);
export const ConnectorSyncOfferSchema = z
    .object({
    syncOperationId: UuidSchema,
    syncGeneration: DecimalStringSchema,
    syncToken: ConnectorFenceTokenSchema,
    deviceGrantEpoch: DecimalStringSchema,
    accountId: UuidSchema,
    documentId: UuidSchema,
    versionId: UuidSchema.nullable(),
    relativePath: z.string().min(1).max(1000),
    direction: ConnectorSyncDirectionSchema,
    expectedSha256: Sha256Base64UrlSchema.nullable(),
    byteLength: DecimalStringSchema,
})
    .strict();
export const ConnectorSyncStateSchema = z.enum(["queued", "offered", "transferring", "succeeded", "failed", "superseded", "interrupted"]);
export const ConnectorSyncResultStateSchema = z.enum(["succeeded", "failed", "superseded"]);
export const ConnectorSyncResultSchema = z
    .object({
    syncOperationId: UuidSchema,
    syncGeneration: DecimalStringSchema,
    deviceGrantEpoch: DecimalStringSchema,
    state: ConnectorSyncResultStateSchema,
    payloadSha256: Sha256Base64UrlSchema.nullable(),
    byteLength: DecimalStringSchema.nullable(),
    errorCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/).nullable(),
})
    .strict();
export const ConnectorJobSchema = z
    .object({
    id: UuidSchema,
    accountId: UuidSchema,
    state: ConnectorJobStateSchema,
    attemptId: UuidSchema,
    leaseGeneration: DecimalStringSchema,
    createdAt: Rfc3339MillisSchema,
    updatedAt: Rfc3339MillisSchema,
})
    .strict();
export const DocumentDerivativeJobSchema = z
    .object({
    jobId: UuidSchema,
    workspaceId: UuidSchema,
    derivativeId: UuidSchema,
    documentId: UuidSchema,
    state: ConnectorJobStateSchema,
    attemptId: UuidSchema,
    leaseGeneration: DecimalStringSchema,
    errorCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/).nullable(),
    createdAt: Rfc3339MillisSchema,
    updatedAt: Rfc3339MillisSchema,
    cancelledAt: Rfc3339MillisSchema.nullable(),
})
    .strict();
export const DeviceMessageBodySchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("CAPABILITIES"), body: DeviceCapabilitiesSchema }).strict(),
    z.object({ type: z.literal("JOB_OFFER"), body: RedactionJobOfferSchema }).strict(),
    z.object({ type: z.literal("JOB_ACCEPT"), body: RedactionJobAcceptSchema }).strict(),
    z.object({ type: z.literal("JOB_START"), body: RedactionJobStartSchema }).strict(),
    z.object({ type: z.literal("LEASE_RENEW"), body: RedactionJobLeaseRenewSchema }).strict(),
    z.object({ type: z.literal("JOB_RESULT"), body: RedactionJobResultSchema }).strict(),
    z.object({ type: z.literal("RESULT_ACK"), body: RedactionJobResultAckSchema }).strict(),
    z.object({ type: z.literal("JOB_CANCEL"), body: RedactionJobCancelSchema }).strict(),
    z.object({ type: z.literal("JOB_CANCELLED"), body: RedactionJobCancelledSchema }).strict(),
    z.object({ type: z.literal("FOCUS_REQUEST_START"), body: FocusTransferStartSchema }).strict(),
    z.object({ type: z.literal("FOCUS_REQUEST_CHUNK"), body: FocusTransferChunkSchema }).strict(),
    z.object({ type: z.literal("FOCUS_DELTA"), body: FocusConnectorDeltaSchema }).strict(),
    z.object({ type: z.literal("FOCUS_RESULT_START"), body: FocusTransferStartSchema }).strict(),
    z.object({ type: z.literal("FOCUS_RESULT_CHUNK"), body: FocusTransferChunkSchema }).strict(),
    z.object({ type: z.literal("FOCUS_CANCEL"), body: FocusConnectorCancelSchema }).strict(),
]);
export function parseDeviceMessageBody(type, body) {
    return DeviceMessageBodySchema.parse({ type, body }).body;
}
//# sourceMappingURL=connector.js.map